import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { renewalAdminAPI } from '../services/api';

// @chronic-renewal-v1 (29 Sep 2026)
// Prescription renewals («Επαναλήψεις Συνταγών») for a month: volume, answer
// times against the 12-hour promise, refunds, and per-doctor approval rates
// (the check on the refund rule: a doctor earns nothing on a refunded request,
// so an unusually high approval rate is worth a look).
//
// Admin never sees medicines or questionnaire answers — the backend does not
// send them. Clinical content stays with the doctor who handled the request.

const th = { textAlign: 'left', padding: '10px 12px', fontSize: 12, color: '#6b7280', fontWeight: 700, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' };
const td = { padding: '10px 12px', fontSize: 13, borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' };

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthRange = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return [`${ym}-01`, `${ym}-${String(last).padStart(2, '0')}`];
};
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('el-GR', { timeZone: 'Europe/Athens', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const STATUS = {
  open: ['Στην ουρά', '#E07B2A'],
  claimed: ['Σε εξέλιξη', '#1E3A5F'],
  info_requested: ['Αναμονή ασθενή', '#6b7280'],
  completed: ['Ολοκληρώθηκε', '#0D9A6A'],
  expired: ['Έληξε', '#6b7280'],
};
const OUTCOME = {
  approved_all: ['Εγκρίθηκαν όλα', '#0D9A6A'],
  partial: ['Μερική έγκριση', '#E07B2A'],
  rejected_all: ['Απορρίφθηκαν όλα', '#B91C1C'],
};
const REFUND = {
  refunded: ['Επιστράφηκαν', '#C0185A'],
  pending: ['Επιστροφή σε εξέλιξη', '#E07B2A'],
  failed: ['ΑΠΕΤΥΧΕ — χειροκίνητα στο Viva', '#B91C1C'],
};

const Pill = ({ text, color }) => (
  <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                 color, background: color + '18', whiteSpace: 'nowrap' }}>{text}</span>
);
const Stat = ({ value, label, warn }) => (
  <div style={{ background: '#fff', border: `1px solid ${warn ? '#FCA5A5' : '#e5e7eb'}`, borderRadius: 10, padding: '12px 16px', minWidth: 130, flex: '1 1 130px' }}>
    <div style={{ fontSize: 22, fontWeight: 700, color: warn ? '#B91C1C' : '#0F1F3D' }}>{value}</div>
    <div style={{ fontSize: 12, color: '#6b7280' }}>{label}</div>
  </div>
);

export default function RenewalsReportPage() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const [from, to] = monthRange(month);
    renewalAdminAPI.list(from, to)
      .then(({ data: res }) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) { setData(null); toast.error('Αδυναμία φόρτωσης επαναλήψεων συνταγών'); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month]);

  const s = data?.summary;
  const all = data?.requests || [];
  const rows = all.filter((r) => {
    if (filter === 'refunded') return r.refunded || r.refundStatus === 'failed' || r.refundStatus === 'pending';
    if (filter === 'late') return r.lateOverSla;
    if (filter === 'live') return ['open', 'claimed', 'info_requested'].includes(r.status);
    return true;
  });
  const euros = (n) => `€${Number(n).toFixed(2)}`;
  // Refunded payments are already status 'refunded', so summing the completed
  // ones IS the kept revenue — do not subtract refunds a second time.
  const keptRevenue = all.filter((r) => r.paymentStatus === 'completed').reduce((t, r) => t + Number(r.amountEuros), 0);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Επαναλήψεις Συνταγών</h1>
          <p style={{ color: '#6b7280', fontSize: 13, margin: '6px 0 0' }}>
            Αιτήματα που πληρώθηκαν τον μήνα (ώρα Αθήνας). Χωρίς φάρμακα και απαντήσεις ασθενών — αυτά τα βλέπει μόνο ο γιατρός.
          </p>
        </div>
        <label style={{ fontSize: 13, color: '#374151' }}>
          Μήνας{' '}
          <input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
                 style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }} />
        </label>
      </div>

      {s && s.refundFailed > 0 && (
        <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>
          {s.refundFailed} επιστροφή/ές απέτυχαν στο Viva και θέλουν χειροκίνητη επιστροφή (έχει σταλεί email στο support).
        </div>
      )}

      {s && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 18 }}>
          <Stat value={s.paid} label="Πληρωμένα αιτήματα" />
          <Stat value={s.completed} label="Ολοκληρώθηκαν" />
          <Stat value={s.open} label="Σε εξέλιξη τώρα" />
          <Stat value={s.lateOverSla} label="Πάνω από 12 ώρες" warn={s.lateOverSla > 0} />
          <Stat value={s.refunded} label="Επιστροφές €10" />
          <Stat value={s.under28Days} label="Εκτέλεση < 28 ημ." />
          <Stat value={s.avgReviewMinutes === null ? '—' : `${s.avgReviewMinutes}′`} label="Μέσος χρόνος γιατρού" />
          <Stat value={euros(keptRevenue)} label="Εισπράξεις (χωρίς τις επιστροφές)" />
        </div>
      )}

      <h2 style={{ fontSize: 16, fontWeight: 700, margin: '4px 0 8px' }}>Ανά γιατρό</h2>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto', marginBottom: 22 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Γιατρός</th>
              <th style={th}>Αιτήματα</th>
              <th style={th}>Φάρμακα εγκρ. / απορ.</th>
              <th style={th}>Ποσοστό έγκρισης</th>
              <th style={th}>Επιστροφές</th>
              <th style={th}>Μέσος χρόνος</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td style={td} colSpan={6}>Φόρτωση…</td></tr>}
            {!loading && (data?.byDoctor || []).length === 0 && (
              <tr><td style={td} colSpan={6}>Δεν υπάρχουν ολοκληρωμένα αιτήματα αυτόν τον μήνα.</td></tr>
            )}
            {!loading && (data?.byDoctor || []).map((d) => (
              <tr key={d.doctor}>
                <td style={td}>{d.doctor}</td>
                <td style={td}>{d.completed}</td>
                <td style={td}>{d.itemsApproved} / {d.itemsRejected}</td>
                <td style={td}>{d.approvalRate === null ? '—' : `${d.approvalRate}%`}</td>
                <td style={td}>{d.refunded}</td>
                <td style={td}>{d.avgReviewMinutes === null ? '—' : `${d.avgReviewMinutes}′`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, margin: '4px 0 8px' }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>Αιτήματα</h2>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[['all', 'Όλα'], ['live', 'Σε εξέλιξη'], ['refunded', 'Επιστροφές'], ['late', 'Πάνω από 12ω']].map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)}
              style={{ padding: '5px 12px', borderRadius: 16, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                       border: '1px solid ' + (filter === k ? '#C0185A' : '#e5e7eb'),
                       background: filter === k ? '#C0185A' : '#fff', color: filter === k ? '#fff' : '#374151' }}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Πληρωμή</th>
              <th style={th}>Ασθενής</th>
              <th style={th}>Γιατρός</th>
              <th style={th}>Κατάσταση</th>
              <th style={th}>Φάρμακα</th>
              <th style={th}>Αποτέλεσμα</th>
              <th style={th}>Επιστροφή</th>
              <th style={th}>Απάντηση σε</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td style={td} colSpan={8}>Φόρτωση…</td></tr>}
            {!loading && rows.length === 0 && <tr><td style={td} colSpan={8}>Κανένα αίτημα.</td></tr>}
            {!loading && rows.map((r) => {
              const st = STATUS[r.status] || [r.status, '#6b7280'];
              const oc = r.outcome ? OUTCOME[r.outcome] : null;
              const rf = REFUND[r.refundStatus];
              return (
                <tr key={r.id} style={{ background: r.refundStatus === 'failed' ? '#FEF2F2' : 'transparent' }}>
                  <td style={td}>{fmtDateTime(r.paidAt)}</td>
                  <td style={td}>{r.patientName}</td>
                  <td style={td}>{r.doctorName || '—'}</td>
                  <td style={td}><Pill text={st[0]} color={st[1]} /></td>
                  <td style={td}>{r.status === 'completed' ? `${r.approvedCount} / ${r.itemCount}` : r.itemCount}</td>
                  <td style={td}>
                    {oc ? <Pill text={oc[0]} color={oc[1]} /> : '—'}
                    {r.under28Days && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>εκτέλεση &lt; 28 ημ. · χωρίς επιστροφή</div>}
                  </td>
                  <td style={td}>{rf ? <Pill text={rf[0]} color={rf[1]} /> : '—'}</td>
                  <td style={td}>
                    {r.hoursToAnswer === null ? '—' : (
                      <span style={{ color: r.lateOverSla ? '#B91C1C' : '#0F1F3D', fontWeight: r.lateOverSla ? 700 : 400 }}>
                        {r.hoursToAnswer} ώρες
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p style={{ color: '#6b7280', fontSize: 12, marginTop: 14 }}>
        Γιατροί στην υπηρεσία: <Link to="/doctors">Ιατροί</Link> → γιατρός → Κλινικές → «Επανάληψη συνταγής».
        Η αμοιβή (€10, γιατρός €6 / DocNow €4) ορίζεται από την υπηρεσία, όχι από το πεδίο τιμής της σύζευξης.
      </p>
    </div>
  );
}
